import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import AdminShiftManagement from './AdminShiftManagement';
import ShiftReviewDashboard from './ShiftReviewDashboard';
import PolicyHub from './PolicyHub';
import ComplianceTracker from './ComplianceTracker';
import UserManagement from './UserManagement';
import LMSTrainingCenter from './LMSTrainingCenter';
import LMSAdminTraining from './LMSAdminTraining';
import TrainingAssignment from './TrainingAssignment';
import ApplicantReview from './ApplicantReview';
import ClientManagement from './ClientManagement';
import AssignmentsManagement from './AssignmentsManagement';
import ReportingDashboard from './ReportingDashboard';
import BillingManagement from './BillingManagement';
import SupplyManagement from './SupplyManagement';
import PayerIntegrations from './PayerIntegrations';
import ReferralManagement from './ReferralManagement';
import { AdminCalloutDashboard } from './ShiftCoverage';
import NotificationBell from './NotificationBell';
import TenantSelector from './TenantSelector';
import { TutorialHelpIcon } from './TutorialOverlay';
import { 
  FileText, Users, LogOut, Calendar, FolderOpen, Shield, 
  GraduationCap, ClipboardPlus, UserPlus, Heart, Link2, 
  ClipboardCheck, Settings, BarChart3, DollarSign, Package, 
  Building2, UserCheck, AlertTriangle, LayoutDashboard,
  ChevronLeft, ChevronRight, Menu, Bell, HelpCircle
} from 'lucide-react';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ policies: 0, users: 0, trainings: 0, pendingApplicants: 0, clients: 0, complianceIssues: 0, pendingShifts: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSubTab, setActiveSubTab] = useState('employees');
  const [shiftSubTab, setShiftSubTab] = useState('calendar');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Navigation items for sidebar
  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'shifts', label: 'Shift Management', icon: Calendar },
    { id: 'policies', label: 'Policy Hub', icon: FolderOpen },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'clients', label: 'Clients', icon: Heart },
    { id: 'billing', label: 'Billing', icon: DollarSign },
    { id: 'payers', label: 'Payers', icon: Building2 },
    { id: 'supplies', label: 'Supplies', icon: Package },
    { id: 'assignments', label: 'Assignments', icon: Link2 },
    { id: 'referrals', label: 'Referrals', icon: UserCheck },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
  ];

  // Sub-tabs for User Management
  const userSubTabs = [
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'applicants', label: 'Applicants', icon: UserPlus },
    { id: 'compliance', label: 'Compliance', icon: Shield },
    { id: 'training', label: 'LMS / Training', icon: GraduationCap },
    { id: 'lms-admin', label: 'Manage Trainings', icon: Settings },
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [policiesRes, usersRes, trainingsRes, applicantsRes, clientsRes, complianceRes] = await Promise.all([
        api.get('/policies/count'),
        api.get('/users'),
        api.get('/lms/trainings'),
        api.get('/applicants'),
        api.get('/clients'),
        api.get('/compliance/requirements')
      ]);
      
      let complianceIssues = 0;
      if (complianceRes.data && Array.isArray(complianceRes.data)) {
        complianceIssues = complianceRes.data.filter(r => r.status === 'expired' || r.status === 'warning').length;
      }
      
      setStats({
        policies: policiesRes.data?.count || 0,
        users: usersRes.data?.length || 0,
        trainings: trainingsRes.data?.length || 0,
        pendingApplicants: applicantsRes.data?.filter(a => a.status === 'pending')?.length || 0,
        clients: clientsRes.data?.length || 0,
        complianceIssues: complianceIssues,
        pendingShifts: 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const renderUserSubContent = () => {
    switch (activeSubTab) {
      case 'employees': return <UserManagement />;
      case 'applicants': return <ApplicantReview />;
      case 'compliance': return <ComplianceTracker />;
      case 'training': return <LMSTrainingCenter />;
      case 'lms-admin': return <LMSAdminTraining />;
      default: return <UserManagement />;
    }
  };

  const renderShiftSubContent = () => {
    switch (shiftSubTab) {
      case 'calendar': return <AdminShiftManagement />;
      case 'review': return <ShiftReviewDashboard />;
      case 'callouts': return <AdminCalloutDashboard />;
      default: return <AdminShiftManagement />;
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview stats={stats} setActiveTab={setActiveTab} />;
      case 'shifts':
        return (
          <div className="space-y-6">
            <div className="flex gap-2 border-b border-slate-200 pb-4">
              {[
                { id: 'calendar', label: 'Shift Calendar' },
                { id: 'review', label: 'Review Shifts' },
                { id: 'callouts', label: 'Callouts & Coverage' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setShiftSubTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    shiftSubTab === tab.id 
                      ? 'bg-blue-600 text-white' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`shift-subtab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {renderShiftSubContent()}
          </div>
        );
      case 'policies':
        return <PolicyHub />;
      case 'users':
        return (
          <div className="space-y-6">
            <div className="flex gap-2 border-b border-slate-200 pb-4 overflow-x-auto">
              {userSubTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSubTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                      activeSubTab === tab.id 
                        ? 'bg-blue-600 text-white' 
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid={`user-subtab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {renderUserSubContent()}
          </div>
        );
      case 'clients':
        return <ClientManagement />;
      case 'billing':
        return <BillingManagement />;
      case 'payers':
        return <PayerIntegrations />;
      case 'supplies':
        return <SupplyManagement />;
      case 'assignments':
        return <AssignmentsManagement />;
      case 'referrals':
        return <ReferralManagement />;
      case 'reports':
        return <ReportingDashboard />;
      default:
        return <DashboardOverview stats={stats} setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full bg-white border-r border-slate-200 transition-all duration-300 z-40 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo / Brand */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Integrity
                </h1>
                <p className="text-xs text-slate-500">Home Care</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive 
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
                title={sidebarCollapsed ? item.label : undefined}
                data-testid={`nav-${item.id}`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : ''}`} />
                {!sidebarCollapsed && (
                  <span className="text-sm truncate">{item.label}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
            title={sidebarCollapsed ? 'Logout' : undefined}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 h-16">
          <div className="h-full px-6 flex items-center justify-between">
            {/* Page Title */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {navItems.find(n => n.id === activeTab)?.label || 'Dashboard'}
              </h2>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              {/* Tenant Selector */}
              <TenantSelector />
              
              {/* Help Icon */}
              <TutorialHelpIcon />
              
              {/* Notifications */}
              <NotificationBell />

              {/* User Profile */}
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

// Dashboard Overview Component
function DashboardOverview({ stats, setActiveTab }) {
  const statCards = [
    { 
      label: 'Documents', 
      value: stats.policies, 
      icon: FileText, 
      color: 'blue',
      action: () => setActiveTab('policies'),
      actionLabel: 'View Policy Hub'
    },
    { 
      label: 'Employees', 
      value: stats.users, 
      icon: Users, 
      color: 'indigo',
      action: () => setActiveTab('users'),
      actionLabel: 'Manage Employees'
    },
    { 
      label: 'Trainings', 
      value: stats.trainings, 
      icon: GraduationCap, 
      color: 'purple',
      action: () => setActiveTab('users'),
      actionLabel: 'View LMS'
    },
    { 
      label: 'Active Clients', 
      value: stats.clients, 
      icon: Heart, 
      color: 'rose',
      action: () => setActiveTab('clients'),
      actionLabel: 'Manage Clients'
    },
    { 
      label: 'Pending Applicants', 
      value: stats.pendingApplicants, 
      icon: UserPlus, 
      color: 'amber',
      action: () => setActiveTab('users'),
      actionLabel: 'Review Applicants'
    },
  ];

  const colorClasses = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-100' },
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-100' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-100' },
    rose: { bg: 'bg-rose-50', icon: 'text-rose-600', border: 'border-rose-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-100' },
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard Overview
        </h1>
        <p className="text-slate-500 mt-1">
          Quick access to key metrics and management areas
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards.map((card, idx) => {
          const Icon = card.icon;
          const colors = colorClasses[card.color];
          return (
            <Card 
              key={idx}
              className={`p-6 bg-white border ${colors.border} hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer`}
              onClick={card.action}
              data-testid={`stat-card-${card.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    {card.label}
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {card.value}
                  </p>
                </div>
                <div className={`p-2.5 rounded-lg ${colors.bg}`}>
                  <Icon className={`w-5 h-5 ${colors.icon}`} />
                </div>
              </div>
              <p className="text-xs text-blue-600 font-medium mt-4 hover:text-blue-700">
                {card.actionLabel} →
              </p>
            </Card>
          );
        })}
      </div>

      {/* Policy Hub Preview */}
      <Card className="p-6 bg-white border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Policy Hub
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              View and manage company policies and procedures
            </p>
          </div>
          <Button
            onClick={() => setActiveTab('policies')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="view-policies-btn"
          >
            View All Policies
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Total Documents</p>
                <p className="text-2xl font-bold text-slate-900">{stats.policies}</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FolderOpen className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Categories</p>
                <p className="text-2xl font-bold text-slate-900">1</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <Card className="p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Schedule Shift', icon: Calendar, tab: 'shifts' },
            { label: 'Add Employee', icon: UserPlus, tab: 'users' },
            { label: 'New Client', icon: Heart, tab: 'clients' },
            { label: 'View Reports', icon: BarChart3, tab: 'reports' },
          ].map((action, idx) => {
            const Icon = action.icon;
            return (
              <button
                key={idx}
                onClick={() => setActiveTab(action.tab)}
                className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 text-left"
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Icon className="w-4 h-4 text-slate-600" />
                </div>
                <span className="text-sm font-medium text-slate-700">{action.label}</span>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
